import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * SecurityCoverageHeatmap - Security coverage matrix by product category or ZT pillar
 *
 * Displays agency × category/pillar matrix with:
 *   - spend: Single-counted dollars (via is_primary_for_spend)
 *   - mappingTier: Visual treatment for curated/inferred/unmapped
 *   - Reconciliation: Cells sum to account total (built-in check)
 *
 * Two modes:
 *   - Tab A: Product categories (NGFW, SIEM, etc.)
 *   - Tab B: Zero Trust pillars (IDENTITY, DEVICE, etc.)
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { securityCategoryShort } from './securityCategories.js';
/** Format currency for display */
// A cell narrower than this cannot hold "$39.6M"; a row label narrower than
// this cannot hold a vendor name. Both matched against the live DOM.
const COL_MIN = 56;
const ROW_LABEL_MIN = 120;
// A body row measured in the live DOM: a vendor name over its total, 54px.
const ROW_H = 54;
// Below three rows the matrix stops being a comparison, so it keeps three and
// lets them scroll rather than showing one vendor and calling it coverage.
const ROW_MIN = 3;
const formatCurrency = (value) => {
    if (value >= 1000000000)
        return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000)
        return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000)
        return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
};
/** Calculate intensity (0-1) based on color mode */
const calculateIntensity = (spend, maxSpend, mode) => {
    if (spend <= 0 || maxSpend <= 0)
        return 0;
    switch (mode) {
        case 'log':
            return Math.log10(Math.abs(spend) + 1) / Math.log10(maxSpend + 1);
        case 'linear':
            return Math.abs(spend) / maxSpend;
        default:
            return Math.abs(spend) / maxSpend;
    }
};
/**
 * Which colour family a cell belongs to, and how saturated it is.
 *
 * The palette itself — indigo for curated, amber for inferred, zinc for
 * unmapped, red for credits — lives in charts.css as RGB triples, not here.
 * This returns the two things the stylesheet cannot know from the DOM: the
 * tier (a class) and the alpha (a custom property carrying intensity). Change
 * a colour in the sheet and every cell, swatch and gradient follows.
 */
const cellTone = (tier, intensity, spend) => {
    if (spend < 0)
        return { tier: 'credit', alpha: Math.min(0.7, intensity * 0.6 + 0.2) };
    switch (tier) {
        case 'curated':
            return { tier: 'curated', alpha: Math.min(0.9, intensity * 0.75 + 0.15) };
        case 'inferred':
            return { tier: 'inferred', alpha: Math.min(0.8, intensity * 0.65 + 0.15) };
        case 'unmapped':
        default:
            return { tier: 'unmapped', alpha: Math.min(0.7, intensity * 0.55 + 0.15) };
    }
};
export function SecurityCoverageHeatmap({ rows, columns, cells, maxCellSpend, summary, maxHeight = 500, fill = false, colorMode = 'log', showValues = true, onCellClick, loading = false, rowHeader = 'Agency', }) {
    // Build cell lookup map
    /*
     * TWO MEASUREMENTS, because both were guesses and both were wrong on a board
     * cell. Ryan: "This widget is broken, can't scroll properly, the headers are
     * always on top of the chart."
     *
     * 1. The column-header row pinned itself at a hard-coded `top: 34px` under
     *    the group row. Measured in the live DOM, that group row is 30px — so the
     *    two sticky rows sat 4px apart and body rows showed through the seam
     *    while scrolling. A fixed offset against an element whose height depends
     *    on its font and padding is a guess; this measures it.
     *
     * 2. The rotated header was a fixed 132px (145.5px with padding). In a board
     *    cell whose scroller measured 382px, that is 46% of the viewport spent on
     *    labels, pinned, with the rows crawling through the strip left over. On
     *    the full-page tab there is room for it; in a widget there is not, so the
     *    header takes a SHARE of what it has rather than a constant, and switches
     *    to the short category names when it gets tight — "Vuln" rather than
     *    "Vuln/Exposure Mg…", which is the same word count in a third of the
     *    height.
     */
    const scrollerRef = useRef(null);
    const groupRowRef = useRef(null);
    const [groupRowHeight, setGroupRowHeight] = useState(30);
    const [scrollerHeight, setScrollerHeight] = useState(0);
    const [scrollerWidth, setScrollerWidth] = useState(0);
    const measure = useCallback(() => {
        if (groupRowRef.current) {
            setGroupRowHeight(Math.round(groupRowRef.current.getBoundingClientRect().height));
        }
        if (scrollerRef.current) {
            const r = scrollerRef.current.getBoundingClientRect();
            setScrollerHeight(Math.round(r.height));
            setScrollerWidth(Math.round(r.width));
        }
    }, []);
    // Layout effect, not effect: the sticky offset is a position, and correcting
    // it after paint shows one frame of misaligned headers on every resize.
    useLayoutEffect(() => {
        measure();
        if (typeof ResizeObserver === 'undefined')
            return undefined;
        const ro = new ResizeObserver(measure);
        if (scrollerRef.current)
            ro.observe(scrollerRef.current);
        if (groupRowRef.current)
            ro.observe(groupRowRef.current);
        return () => ro.disconnect();
    }, [measure]);
    /**
     * How much vertical room the rotated labels may take.
     *
     * A QUARTER of the scroller, floored at 62px and capped at the 132px the
     * full-page tab was designed around. The quarter is not arbitrary: rows are
     * 38px, so on the 382px scroller a board cell actually gets, this leaves
     * (382 - 30 group - 107 header) / 38 = six rows visible. At a third it was
     * five, and at the old fixed 132px the header and group row together took
     * 46% of the viewport — which is what "the headers are always on top of the
     * chart" describes.
     *
     * 62px is where a rotated label stops being legible; it fits the short names
     * ("Firewall", "Identity", "Endpoint" are all eight characters) and nothing
     * longer, which is why the switch below happens above that floor rather than
     * at it.
     */
    const headerHeight = scrollerHeight > 0
        ? Math.max(62, Math.min(132, Math.round(scrollerHeight * 0.25)))
        : 132;
    /** Below this there is no room for "Vuln/Exposure Mgmt" set vertically. */
    const useShortLabels = headerHeight < 110;
    /*
     * WHICH COLUMNS FIT — and the empty ones go first.
     *
     * Measured 2026-09-14: the matrix drew all fifteen categories at every size.
     * In a 343px widget that is an 11px cell, where 27 values clipped to nothing
     * and the box still overflowed. The footprint beside it sheds years to fit;
     * this shed nothing.
     *
     * A category no vendor here spends on is a column of dashes: it costs the
     * same width as a real one and carries no information, so it is dropped
     * first. Only when every remaining column still will not fit does this take
     * the smallest by spend, and what was dropped is stated under the table.
     */
    const colFit = useMemo(() => {
        const spendByCol = new Map();
        for (const c of cells) {
            spendByCol.set(String(c.colId), (spendByCol.get(String(c.colId)) || 0) + Math.abs(c.spend || 0));
        }
        // No layout yet (jsdom, first paint): show everything rather than compute
        // a fit from a zero width and silently hide data.
        if (!scrollerWidth)
            return { shown: columns, droppedEmpty: 0, droppedWithSpend: 0 };
        const room = scrollerWidth - ROW_LABEL_MIN;
        const canFit = Math.max(1, Math.floor(room / COL_MIN));
        if (columns.length <= canFit)
            return { shown: columns, droppedEmpty: 0, droppedWithSpend: 0 };
        const withSpend = columns.filter((c) => (spendByCol.get(String(c.id)) || 0) > 0);
        const empty = columns.length - withSpend.length;
        if (withSpend.length <= canFit) {
            return { shown: withSpend, droppedEmpty: empty, droppedWithSpend: 0 };
        }
        // Still too many: keep the biggest, but draw them in the original order so
        // the categories do not reshuffle as the widget resizes.
        const keep = new Set([...withSpend]
            .sort((a, b) => (spendByCol.get(String(b.id)) || 0) - (spendByCol.get(String(a.id)) || 0))
            .slice(0, canFit)
            .map((c) => String(c.id)));
        return {
            shown: withSpend.filter((c) => keep.has(String(c.id))),
            droppedEmpty: empty,
            droppedWithSpend: withSpend.length - canFit,
        };
    }, [columns, cells, scrollerWidth]);
    const shownColumns = colFit.shown;
    const cellMap = useMemo(() => {
        const map = new Map();
        cells.forEach((cell) => {
            map.set(`${cell.rowId}::${cell.colId}`, cell);
        });
        return map;
    }, [cells]);
    // Calculate max spend for intensity scaling
    const maxSpend = useMemo(() => {
        return maxCellSpend ?? Math.max(...cells.map((c) => Math.abs(c.spend)), 1);
    }, [cells, maxCellSpend]);
    // Group columns by category
    const columnGroups = useMemo(() => {
        const groups = [];
        let currentGroup = '';
        shownColumns.forEach((col) => {
            const group = col.group || '';
            if (group !== currentGroup) {
                groups.push({ name: group, cols: [col] });
                currentGroup = group;
            }
            else if (groups.length > 0) {
                groups[groups.length - 1].cols.push(col);
            }
            else {
                groups.push({ name: '', cols: [col] });
            }
        });
        return groups;
    }, [shownColumns]);
    /*
     * A group row earns its 30px only when it GROUPS something. On the live
     * payload exactly one column carries a group name — "Other" over "Unmapped"
     * — so the board was paying a second sticky header row, 8% of the scroll
     * viewport, to write one word above one column. One named group is a label,
     * not a grouping; two or more is a grouping.
     */
    const hasColumnGroups = columnGroups.filter((g) => g.name).length > 1;
    /*
     * HOW MANY ROWS FIT — and the rest are COUNTED, not hidden behind a scrollbar.
     *
     * Measured 2026-09-14 on a board cell: the scroller is 392px, the content
     * 1062px, so 670px of vendors sat below the fold. Five of eighteen rows were
     * visible and the other thirteen were reachable only by discovering a scroll
     * region nested inside a widget inside a scrolling page. A reader who never
     * finds it reads five vendors as the whole security estate.
     *
     * Rows arrive biggest-first, so the cap keeps the ones that matter and the
     * note underneath says what it kept back, with their combined spend — which
     * is the number that tells you whether the tail is worth opening.
     */
    const rowFit = useMemo(() => {
        // Unmeasured (jsdom, first paint): show every row rather than compute a
        // cap from a zero height and silently drop data.
        if (!scrollerHeight)
            return rows.length;
        const chrome = headerHeight + (hasColumnGroups ? groupRowHeight : 0);
        const n = Math.floor((scrollerHeight - chrome) / ROW_H);
        return Math.max(ROW_MIN, Math.min(rows.length, n));
    }, [scrollerHeight, headerHeight, hasColumnGroups, groupRowHeight, rows.length]);
    const shownRows = useMemo(() => rows.slice(0, rowFit), [rows, rowFit]);
    const droppedRows = rows.length - shownRows.length;
    const droppedRowSpend = useMemo(() => rows.slice(rowFit).reduce((sum, r) => sum + (r.totalSpend || 0), 0), [rows, rowFit]);
    // Get cell style - uses app theme for empty cells, tier colors for data
    const toneFor = (cell, intensity) => cell ? cellTone(cell.mappingTier, intensity, cell.spend) : null;
    const getTooltip = (cell, row, col) => {
        if (!cell) {
            return `${row.label} × ${col.label}: No coverage`;
        }
        const parts = [
            `${row.label} × ${col.label}`,
            `Spend: ${cell.spendFormatted || formatCurrency(cell.spend)}`,
        ];
        if (cell.lineCount !== undefined) {
            parts.push(`Lines: ${cell.lineCount.toLocaleString()}`);
        }
        if (cell.componentCount !== undefined) {
            parts.push(`Components: ${cell.componentCount}`);
        }
        if (cell.mappingTier) {
            parts.push(`Mapping: ${cell.mappingTier}`);
        }
        if (cell.vendors && cell.vendors.length > 0) {
            const vendorList = cell.vendors.slice(0, 3).join(', ');
            const more = cell.vendors.length > 3 ? ` +${cell.vendors.length - 3} more` : '';
            parts.push(`Vendors: ${vendorList}${more}`);
        }
        return parts.join('\n');
    };
    if (loading) {
        return _jsx("div", { className: "sch sch-status", children: "Loading security coverage data..." });
    }
    if (rows.length === 0 || columns.length === 0) {
        return _jsx("div", { className: "sch sch-status", children: "No security coverage data available" });
    }
    /*
     * Every value below that reaches `style` is a CSS custom property carrying a
     * number the stylesheet cannot know: a measured sticky offset, a header
     * height derived from the scroller, a cell's intensity. All static styling
     * is in charts.css under `sch-`. That split is the rule this file used to
     * break 36 times over.
     */
    const stickyTop = hasColumnGroups ? `${groupRowHeight}px` : '0px';
    return (
    /*
     * `fill` marks the OUTERMOST box, which is the contract account-board.css
     * states: the scroller inside flexes to whatever the cell leaves it, so the
     * table scrolls inside the cell instead of the cell clipping the table.
     * (An earlier version put the flex class on the inner scroller, whose
     * parent was a plain block — the table grew to ~1,131px inside a 483px
     * cell and clipped "$1.7M" to "$1.7N".)
     */
    _jsxs("div", { className: `sch${fill ? ' sch--fill ts-chart-fill' : ''}`, children: [summary && (_jsxs("div", { className: "sch-summary", children: [_jsxs("div", { className: "sch-summary__stats", children: [_jsxs("span", { className: "sch-summary__stat", children: [summary.agencyCount, " agencies"] }), _jsxs("span", { className: "sch-summary__stat", children: [summary.categoryCount || summary.pillarCount, " columns"] }), _jsxs("span", { className: "sch-summary__stat sch-summary__stat--total", children: ["Total: ", summary.grandTotalFormatted] })] }), _jsxs("div", { className: `sch-summary__recon ${summary.reconciles ? 'sch-summary__recon--ok' : 'sch-summary__recon--bad'}`, children: [_jsx("span", { className: "sch-summary__mark", children: summary.reconciles ? '✓' : '⚠' }), _jsx("span", { children: summary.reconciles ? 'Reconciled' : `Mismatch: cells = ${summary.cellTotalFormatted}` })] })] })), _jsx("div", { ref: scrollerRef, className: "sch-scroll", style: fill ? undefined : { '--sch-max-h': `${maxHeight}px` }, children: _jsxs("table", { className: "sch-table", children: [hasColumnGroups && (_jsx("thead", { ref: groupRowRef, children: _jsxs("tr", { children: [_jsx("th", { className: "sch-corner" }, "corner"), columnGroups.map((group, gi) => (_jsx("th", { colSpan: group.cols.length, className: `sch-group${group.name ? ' sch-group--named' : ''}`, children: group.name }, gi)))] }) })), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "sch-row-head-th", style: { '--sch-top': stickyTop }, children: rowHeader }), shownColumns.map((col) => {
                                        const text = useShortLabels ? securityCategoryShort(col.label) : col.label;
                                        return (_jsx("th", { className: "sch-col", style: { '--sch-top': stickyTop, '--sch-head-h': `${headerHeight}px` }, 
                                            // The full name is always in the tooltip, whichever is drawn.
                                            title: col.label, children: text.length > 24 ? `${text.slice(0, 24)}…` : text }, col.id));
                                    })] }) }), _jsx("tbody", { children: shownRows.map((row) => (_jsxs("tr", { children: [_jsxs("td", { className: "sch-row-head", title: row.officialName, children: [_jsx("div", { className: "sch-row-label", title: row.label, children: row.label }), row.sublabel && _jsx("div", { className: "sch-row-sub", children: row.sublabel })] }), shownColumns.map((col) => {
                                        const cell = cellMap.get(`${row.id}::${col.id}`);
                                        const hasValue = !!cell && cell.spend !== 0;
                                        const intensity = hasValue
                                            ? calculateIntensity(cell.spend, maxSpend, colorMode)
                                            : 0;
                                        const showText = showValues && hasValue && intensity > 0.25;
                                        const tone = hasValue ? toneFor(cell, intensity) : null;
                                        const classes = [
                                            'sch-cell',
                                            tone ? `sch-cell--${tone.tier}` : 'sch-cell--empty',
                                            cell && onCellClick ? 'sch-cell--live' : '',
                                        ].filter(Boolean).join(' ');
                                        /*
                                         * A DRILLABLE CELL IS A BUTTON.
                                         *
                                         * `onClick` on the `<td>` made this matrix mouse-only: the
                                         * cell took no focus and announced nothing, so the drill it
                                         * opens was unreachable by keyboard and invisible to a screen
                                         * reader. Measured 2026-09-17 — `.focus()` on a `sch-cell`
                                         * left `document.activeElement` unchanged.
                                         *
                                         * `VendorCategoryHeatmap` three widgets away had it right all
                                         * along: a real `<button>` carrying an `aria-label` that
                                         * names the row, the column and the value. This is that,
                                         * including the phrasing, so the two matrices announce alike.
                                         *
                                         * The button sits INSIDE the `<td>` rather than replacing it:
                                         * a cell that is also a button loses the table semantics the
                                         * row and column headers depend on.
                                         */
                                        // Only a cell with money in it is a button. `cell` can exist
                                        // with nothing recorded, and a focus stop that opens an empty
                                        // list is a dead affordance — the footprint draws those as a
                                        // plain span too.
                                        const drillable = !!(cell && hasValue && onCellClick);
                                        const label = `${row.label}, ${col.label} — ${hasValue && cell
                                            ? `${cell.spendFormatted || formatCurrency(cell.spend)}${cell.lineCount ? ` across ${cell.lineCount} lines` : ''}`
                                            : 'nothing recorded'}`;
                                        const inner = (_jsxs(_Fragment, { children: [showText && cell && (_jsx("span", { className: `sch-val${intensity > 0.4 ? ' sch-val--light' : ''}`, children: cell.spendFormatted || formatCurrency(cell.spend) })), !hasValue && _jsx("span", { className: "sch-dash", children: "\u2013" })] }));
                                        return (_jsx("td", { className: classes, style: tone ? { '--sch-a': tone.alpha } : undefined, title: getTooltip(cell, row, col), children: drillable ? (_jsx("button", { type: "button", className: "sch-cell-btn", "aria-label": label, onClick: () => onCellClick(cell, row, col), children: inner })) : (
                                            // A static cell keeps exactly the markup it had: the
                                            // row and column headers already name it, and an
                                            // `aria-label` on a role-less <span> is ignored by
                                            // assistive tech anyway.
                                            inner) }, col.id));
                                    })] }, row.id))) })] }) }), (colFit.droppedEmpty > 0 || colFit.droppedWithSpend > 0 || droppedRows > 0) && (
            /* What is not drawn is STATED. A matrix quietly showing nine of
               fifteen categories, or five of eighteen vendors, invites the reader
               to take what they can see for the whole security estate. */
            /*
             * ONE LINE PER FACT, and each line carries its own remedy.
             *
             * These are two unrelated truncations — vendors cut off the BOTTOM,
             * categories cut off the SIDE — with two different causes and two
             * different fixes. Run together they read as one thought and parse as
             * none:
             *
             *   "13 more rows · $176K between them — open the full view · 4
             *    categories with no spend here are hidden, too narrow to draw"
             *
             * Three separators and two em-dashes in a sentence that changes subject
             * halfway. Split, they are both obvious.
             *
             * "vendors", not "rows": the header already says the axis is
             * `top vendors × category`, and a reader counting rows has to translate.
             * And a width problem is answered by widening, which the reader can
             * actually do on the board — the way the purchase list now says to make
             * itself taller.
             */
            _jsxs("p", { className: "ts-foot sch-foot-trunc", children: [droppedRows > 0 && (_jsxs("span", { className: "sch-foot-line", children: [`${droppedRows} more ${droppedRows === 1 ? 'vendor' : 'vendors'}`, droppedRowSpend > 0 ? `, ${formatCurrency(droppedRowSpend)} between them` : '', ' — open the full view'] })), (colFit.droppedEmpty > 0 || colFit.droppedWithSpend > 0) && (_jsx("span", { className: "sch-foot-line", children: colFit.droppedWithSpend > 0
                            ? `${colFit.droppedWithSpend + colFit.droppedEmpty} categories too narrow to draw, ${colFit.droppedWithSpend} of them hold spend — widen this widget`
                            : `${colFit.droppedEmpty} categories too narrow to draw — none of them hold spend here` }))] })), _jsxs("div", { className: "sch-legend", children: [_jsxs("div", { className: "sch-legend__item sch-legend__item--intensity", children: [_jsx("span", { children: "Intensity:" }), _jsx("div", { className: "sch-gradient" }), _jsxs("span", { children: ["max ", formatCurrency(maxSpend)] })] }), _jsx("div", { className: "sch-legend__group", children: ['curated', 'inferred', 'unmapped', 'credit'].map((tier) => (_jsxs("div", { className: "sch-legend__item", children: [_jsx("div", { className: `sch-swatch sch-swatch--${tier}` }), _jsx("span", { children: tier === 'credit' ? 'Credits' : tier[0].toUpperCase() + tier.slice(1) })] }, tier))) })] })] }));
}
export default SecurityCoverageHeatmap;
